/**
 * Uppmätta AI-kostnader för Sellavo.
 *
 * Siffrorna nedan är härledda ur riktiga anrop i AI Gateway-loggen
 * (google/gemini-3.6-flash, juli 2026). Genom att lösa ut kostnaden mot
 * token-antalen i loggade anrop får vi:
 *   1230 in / 932 out  = 0,035340 krediter
 *   1153 in / 902 out  = 0,033978 krediter
 *   1230 in / 1073 out = 0,039570 krediter
 * => 6 krediter per 1M inmatade tokens, 30 krediter per 1M utmatade tokens.
 */
export const MODEL_RATES = {
  "google/gemini-3.6-flash": { inputPerMillion: 6, outputPerMillion: 30 },
  "google/gemini-2.5-flash-lite": { inputPerMillion: 0.5, outputPerMillion: 2 },
  // Bildmodell: en genererad bild motsvarar ~1290 utmatade tokens.
  "google/gemini-3.1-flash-image": { inputPerMillion: 6, outputPerMillion: 93 },
  // Jämförelsemodeller i skannlabbet – uppskattade nivåer tills vi mätt fler anrop.
  "google/gemini-3.1-pro-preview": { inputPerMillion: 25, outputPerMillion: 200 },
  "openai/gpt-5.6-sol": { inputPerMillion: 30, outputPerMillion: 240 },
} as const;

export type ModelId = keyof typeof MODEL_RATES;

export type AiOperation = {
  id: string;
  name: string;
  description: string;
  model: ModelId;
  /** Genomsnittligt antal tokens in (bild + prompt). */
  inputTokens: number;
  /** Genomsnittligt antal tokens ut. */
  outputTokens: number;
  /** Hur siffrorna tagits fram. */
  source: string;
};

export const AI_OPERATIONS: AiOperation[] = [
  {
    id: "ad",
    name: "Skapa annons med AI",
    description: "Bildgranskning, rubrik, annonstext, kategori och prisförslag i ett anrop.",
    model: "google/gemini-3.6-flash",
    inputTokens: 1205,
    outputTokens: 874,
    source: "Snitt av 7 riktiga anrop i AI Gateway-loggen (1153–1255 in, 717–1073 ut).",
  },
  {
    id: "search",
    name: "AI-sökning",
    description: "Tolkar fritext till sökord, kategori och prisintervall.",
    model: "google/gemini-3.6-flash",
    inputTokens: 350,
    outputTokens: 80,
    source: "Systemprompt + kategorilista + kort JSON-svar (strukturerad output).",
  },
  {
    id: "video_scan_part",
    name: "Videoskanning – en del",
    description:
      "En ~30 s bit av filmen (max 100 MB råfil delas upp i delar) analyseras och prylarna listas.",
    model: "google/gemini-3.6-flash",
    inputTokens: 9500,
    outputTokens: 1400,
    source: "Video ≈ 300 tokens per sekund + prompt; JSON-lista med prylar, skick och värde ut.",
  },
  {
    id: "video_scan_extra",
    name: "Videoskanning – extraanrop",
    description: "Kompletterande svep efter missade prylar och prisförfining av de värdefulla.",
    model: "google/gemini-3.6-flash",
    inputTokens: 6000,
    outputTokens: 900,
    source: "Två lättare uppföljningsanrop per skanning (rescan + refine).",
  },
  {
    id: "photo_scan",
    name: "Fotoskanning (bulk)",
    description: "Upp till 30 uppladdade foton grupperas per pryl med namn och uppskattat värde.",
    model: "google/gemini-3.6-flash",
    inputTokens: 8500,
    outputTokens: 1300,
    source: "≈ 260 tokens per bild × 30 bilder + prompt; strukturerad JSON ut.",
  },
  {
    id: "clean_image",
    name: "Rensa bakgrund på bild",
    description: "Genererar en ny bild med studiobakgrund och skugga.",
    model: "google/gemini-3.1-flash-image",
    inputTokens: 1400,
    outputTokens: 1290,
    source: "En genererad bild motsvarar ~1290 utmatade tokens hos bildmodellen.",
  },
  {
    id: "rewrite",
    name: "AI-omskrivning av annonstext",
    description: "Skriver om rubrik och beskrivning på befintlig annons.",
    model: "google/gemini-3.6-flash",
    inputTokens: 900,
    outputTokens: 700,
    source: "Text in, text ut – ingen bild skickas med.",
  },
  {
    id: "deep-valuation-identify",
    name: "Djupvärdering – identifiering",
    description: "Väger bildträffar och webbunderlag till ett förslag på exakt modell och tillverkare.",
    model: "google/gemini-3.6-flash",
    inputTokens: 3403,
    outputTokens: 1112,
    source: "Snitt av 4 loggade anrop (operation deep-valuation-identify).",
  },
  {
    id: "deep-valuation-identify-verdict",
    name: "Djupvärdering – verifiering",
    description: "Dubbelkollar identifieringen mot bilderna innan värderingen görs.",
    model: "google/gemini-3.6-flash",
    inputTokens: 3390,
    outputTokens: 1049,
    source: "Snitt av 4 loggade anrop (operation deep-valuation-identify-verdict).",
  },
  {
    id: "deep-valuation-questions",
    name: "Djupvärdering – följdfrågor",
    description: "Läser bilderna på prylen och tar fram 3–6 frågor som avgör värdet.",
    model: "google/gemini-3.6-flash",
    inputTokens: 2131,
    outputTokens: 1428,
    source: "Snitt av 6 loggade anrop. Upp till 4 bilder (~260 tokens/bild) + prompt; kort JSON-lista med frågor ut.",
  },
  {
    id: "deep-valuation",
    name: "Djupvärdering – sammanvägning",
    description:
      "Väger samman svar, närbilder och marknadsunderlag till värde i tre nivåer med källor. Marknadssökningen görs hos Perplexity och debiteras separat (~0,01 USD per sökning).",
    model: "google/gemini-3.6-flash",
    inputTokens: 4288,
    outputTokens: 2140,
    source: "Snitt av 3 loggade anrop. Upp till 8 bilder + marknadsunderlag på ~1 200 tokens; utförligt JSON-svar ut.",
  },
];


/** Kostnad i Lovable-krediter för ett anrop. */
export function creditsForOperation(
  op: Pick<AiOperation, "model" | "inputTokens" | "outputTokens">,
): number {
  const rate = MODEL_RATES[op.model];
  return (
    (op.inputTokens / 1_000_000) * rate.inputPerMillion +
    (op.outputTokens / 1_000_000) * rate.outputPerMillion
  );
}

/**
 * Vad en Lovable-kredit kostar oss i kronor.
 *
 * Kalibrerat mot listpriset för google/gemini-3.6-flash: 6 krediter per 1M
 * inmatade tokens motsvarar ~0,30 USD, alltså ~0,05 USD per kredit
 * (≈ 0,55 kr med 11 kr/USD). Tidigare låg värdet på 2,5 kr, vilket gjorde att
 * labbet visade ungefär fem gånger för hög kostnad.
 */
export const DEFAULT_CREDIT_SEK = 0.55;

/** Kostnad för en webbsökning hos Perplexity (~0,01 USD per sökning). */
export const WEB_SEARCH_SEK = 0.11;

/**
 * Externa tjänster som inte prissätts per token utan per anrop.
 * `sekPerCall` är styckepriset, `tokenRate` används när tjänsten ändå
 * rapporterar tokens (Perplexity gör det, Google gör det inte).
 */
export const EXTERNAL_RATES: Record<
  string,
  { service: string; sekPerCall: number; unit: string; tokenRate?: { inputPerMillionSek: number; outputPerMillionSek: number } }
> = {
  "perplexity/sonar-pro": {
    service: "Perplexity",
    sekPerCall: WEB_SEARCH_SEK,
    unit: "sökning",
    // sonar-pro: 3 USD per 1M in, 15 USD per 1M ut (≈ 10,5 kr/USD).
    tokenRate: { inputPerMillionSek: 31, outputPerMillionSek: 157 },
  },
  "sonar-pro": {
    service: "Perplexity",
    sekPerCall: WEB_SEARCH_SEK,
    unit: "sökning",
    tokenRate: { inputPerMillionSek: 31, outputPerMillionSek: 157 },
  },
  "serpapi/google-lens": { service: "SerpApi (Google Lens)", sekPerCall: 0.08, unit: "sökning" },
  "google/vision-web-detection": { service: "Google Vision", sekPerCall: 0.016, unit: "bildanalys" },
  "firecrawl/scrape": { service: "Firecrawl", sekPerCall: 0.05, unit: "sidhämtning" },
};

/** Är modellen en styckeprisad extern tjänst? */
export function externalRate(model: string) {
  return EXTERNAL_RATES[model];
}

/**
 * Kostnad i kronor för ett enskilt anrop: token-modeller räknas via krediter,
 * externa tjänster via styckepris (plus tokens när de rapporteras).
 */
export function sekForCall(
  model: string,
  inputTokens: number,
  outputTokens: number,
  units = 0,
): number {
  const external = EXTERNAL_RATES[model];
  if (external) {
    const tokens = external.tokenRate
      ? (inputTokens / 1_000_000) * external.tokenRate.inputPerMillionSek +
        (outputTokens / 1_000_000) * external.tokenRate.outputPerMillionSek
      : 0;
    return external.sekPerCall * Math.max(1, units) + tokens;
  }
  const rate = MODEL_RATES[model as ModelId] ?? MODEL_RATES["google/gemini-3.6-flash"];
  const credits =
    (inputTokens / 1_000_000) * rate.inputPerMillion +
    (outputTokens / 1_000_000) * rate.outputPerMillion;
  return credits * DEFAULT_CREDIT_SEK;
}

/** Vilken leverantör som ligger bakom en modellsträng. */
export function serviceForModel(model: string): string {
  return EXTERNAL_RATES[model]?.service ?? (model ? "Lovable AI" : "Sellavo");
}

export function formatSek(value: number, decimals = 2): string {
  return `${value.toFixed(decimals).replace(".", ",")} kr`;
}


/* ---------- Lagring och kostnad per annons ---------- */

/** Uppskattad lagringskostnad (Lovable Cloud/Supabase: ~0,021 USD per GB och månad). */
export const STORAGE_SEK_PER_GB_MONTH = 0.22;
/** Uppskattad kostnad för datatrafik ut (~0,09 USD per GB). */
export const EGRESS_SEK_PER_GB = 0.95;

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 MB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/** Lagringskostnad i kronor för en given mängd byte under angivet antal månader. */
export function storageSek(bytes: number, months = 2): number {
  return (bytes / 1024 ** 3) * STORAGE_SEK_PER_GB_MONTH * months;
}

/* ---------- Namn på modeller i kostnadstabeller ---------- */

const MODEL_LABELS: Record<string, string> = {
  "google/gemini-3.6-flash": "Gemini 3.6 Flash",
  "google/gemini-3.5-flash": "Gemini 3.5 Flash",
  "google/gemini-2.5-flash-lite": "Gemini 2.5 Flash Lite",
  "google/gemini-3.1-flash-image": "Gemini 3.1 Flash Image",
  "google/gemini-3.1-pro-preview": "Gemini 3.1 Pro",
  "openai/gpt-5.6-sol": "GPT-5.6 Sol",
  "perplexity/sonar-pro": "sonar-pro",
  "sonar-pro": "sonar-pro",
  "serpapi/google-lens": "Google Lens",
  "google/vision-web-detection": "Vision",
  "google/vision-read": "Vision",
  "firecrawl/scrape": "scrape",
};

/** Kort och läsbart namn på en modell. */
export function modelLabel(model: string): string {
  if (!model) return "";
  return MODEL_LABELS[model] ?? model.split("/").pop() ?? model;
}

/** Radrubrik i kostnadstabellen: tjänst plus modell, t.ex. "Lovable AI · Gemini 3.6 Flash". */
export function costRowLabel(model: string, fallbackService = "Sellavo"): string {
  if (!model) return fallbackService;
  const label = modelLabel(model);
  const service = serviceForModel(model);
  return label ? `${service} · ${label}` : service;
}
